import { useContext, useEffect, useRef, useState } from 'react'
import { AppContext, AppContextType } from '../AppContext'
import { persistent } from '../services/LocalStorage'
import { useTranslation } from 'react-i18next'
import CrossPlatformDialog from '@/dialogs/components/CrossPlatformDialog'
import { DialogBody } from '@/components/Dialog'
import DialogActionButtonsSection from '@/dialogs/components/DialogActionButtonsSection'

function makeDoNotShowAgainId(id: string) {
    return 'promptdialog:dnsa:' + id
}

function PromptDialogManager() {
    const { t } = useTranslation()

    const { state } = useContext(AppContext) as AppContextType

    const [show, _setShow] = useState<boolean>(false)
    const showRef = useRef<boolean>(false)

    const setShow = (v: boolean) => {
        _setShow(v)
        showRef.current = v
    }

    const showTimer = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
        if (!state.promptDialogState) {
            return
        }
        if (persistent.getBoolean(makeDoNotShowAgainId(state.promptDialogState.id))) {
            return
        }

        if (showTimer.current) {
            clearTimeout(showTimer.current)
        }

        // if already showing:
        if (showRef.current) {
            setShow(false)
            showTimer.current = setTimeout(() => {
                setShow(true)
            }, 500)
        } else {
            setShow(true)
        }
    }, [state.promptDialogState])

    if (show && state.promptDialogState != null) {
        const prompt = state.promptDialogState

        const rightButtonText = prompt.positiveButton || t('OK') || 'OK'
        const leftButtonText = (() => {
            if (prompt.negativeButtonDoNotShowAgain) {
                return t(`Don't show again`) || `Don't show again`
            } else {
                return prompt.negativeButton || undefined
            }
        })()

        const onLeftButtonClicked =
            prompt.negativeButton || prompt.negativeButtonDoNotShowAgain
                ? () => {
                      setShow(false)
                      if (prompt.negativeButtonDoNotShowAgain) {
                          persistent.set(makeDoNotShowAgainId(prompt.id), true)
                      }
                  }
                : undefined

        return (
            <CrossPlatformDialog
                frameName={'Dialog:' + state.promptDialogState.id}
                onDefaultClose={() => setShow(false)}
                browserZIndex={28}
            >
                <DialogBody>{prompt.text || ''}</DialogBody>

                <DialogActionButtonsSection
                    leftButtonText={leftButtonText}
                    rightButtonText={rightButtonText}
                    onLeftButtonClicked={onLeftButtonClicked}
                    onRightButtonClicked={() => setShow(false)}
                />
            </CrossPlatformDialog>
        )
    } else {
        return null
    }
}

export default PromptDialogManager
